import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer, Tabs, Table, Tag, Button, Input, message, Spin, Modal,
  Popconfirm, Select, Empty, Switch, Timeline, Space, AutoComplete, Tooltip,
  Checkbox, DatePicker,
} from "antd";
import {
  CloseOutlined, ShopOutlined, CheckCircleFilled, MinusCircleOutlined,
  MailOutlined, PhoneOutlined, PlusOutlined, ThunderboltOutlined,
  SafetyCertificateOutlined, DeleteOutlined, EnvironmentOutlined,
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";

import * as api from "./controller";
import { NUMERIC, tileSparkOptions } from "./analytics/chartTheme";
import "./analytics/analytics.css";
import OfficeUsersPanel from "./OfficeUsersPanel";
import HtmlBodyEditor from "./HtmlBodyEditor";
import ContactCarousel from "./ContactCarousel";
import EmailFeed from "./EmailFeed";
import OfficeFtcAccessPanel from "./OfficeFtcAccessPanel";
import OfficeReportPanel from "./OfficeReportPanel";
import OfficeFeaturesPanel from "./OfficeFeaturesPanel";
import OfficeBreakdownFormatPanel from "./OfficeBreakdownFormatPanel";
import OfficeBillingPanel from "./billing/OfficeBillingPanel";
import CompleteSetupModal from "./billing/CompleteSetupModal";
import { NOT_APPLICABLE_LABEL, isApplicable, isFullyReady, planNote, readinessCounts } from "./lib/readiness";
import MarkDormantWizard from "./MarkDormantWizard";
import { PracticeTagEditor } from "./LeadTagPicker";
import { tourTag, tourHowFar, TOTAL_STEPS } from "./tourLabel";
import "./crm.css";
import "./dormant.css";
import "./mailboxes.css";

const INVITE_COLOR = { pending: "gold", accepted: "green", rejected: "red", cancelled: "default" };
const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

const STAGE_COLOR = {
  not_started: "default", no_owner: "default", signed_up: "blue",
  practice_setup: "geekblue", practice_created: "cyan", office_setup: "purple",
  banking: "gold", inviting_users: "orange", subscription: "magenta",
  sikka_setup: "geekblue", portal_credentials: "volcano", procedure_code_defaults: "lime",
  completed: "green",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// Matches the office-facing Portal Credentials panel so the same session reads
// the same way on both screens.
const timeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// The saved browser session is what stops a scrape being challenged for a code;
// without one the next run will stall on a 2FA prompt. Only the two automated
// portals have any of this — manual portals are signed into by a person.
function TwoFactorCell({ tf }) {
  if (!tf || !tf.applicable) return <span className="crm-sub">—</span>;
  if (!tf.configured) return <span className="crm-sub">No login yet</span>;

  const age = timeAgo(tf.session_saved_at);
  const route = [tf.method, tf.destination].filter(Boolean).join(" · ");
  return (
    <div className="crm-cell-stack">
      {age ? (
        <Tag color="green">Session active · {age}</Tag>
      ) : (
        <Tooltip title="The next scrape will be asked for a 2FA code.">
          <Tag color="orange">No session</Tag>
        </Tooltip>
      )}
      {route ? <span className="crm-sub">2FA → {route}</span> : <span className="crm-sub">No 2FA route set</span>}
      {tf.accounts > 1 ? (
        <span className="crm-sub">
          {tf.accounts_with_session} of {tf.accounts} accounts with a session
        </span>
      ) : null}
    </div>
  );
}

function initials(name) {
  if (!name) return "—";
  const p = name.trim().split(/\s+/);
  return ((p[0][0] || "") + (p[1] ? p[1][0] : "")).toUpperCase();
}

// Three headline figures with a trend, each a link into the Analytics page
// pre-filtered to this office. Loads independently of the drawer payload so a
// slow aggregate never holds up the rest of the panel.
function ActivitySummary({ officeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!officeId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await api.getOfficeActivity(officeId, { period: "daily", buckets: 30 });
      if (!cancelled) {
        setData(res && res.status >= 200 && res.status < 300 ? res.json : null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [officeId]);

  if (loading && !data) return <Spin />;
  if (!data) return <div className="crm-empty">Activity isn&rsquo;t available for this office.</div>;

  // Hash-routed app (createHashHistory) — a bare "/crm-analytics" would trigger a
  // full document load whose hash is empty, so the router would resolve "/" and
  // the office would be silently dropped.
  const analyticsHref = `#/crm-analytics?office=${officeId}&period=daily`;
  const open = () => { window.location.href = analyticsHref; };

  const series = data.series || {};
  const buckets = data.buckets || [];
  // Small readers instead of optional chaining — the rest of this codebase
  // doesn't use `?.`, and the build toolchain here is old enough to be worth
  // not finding out about.
  const total = (key) => ((series[key] && series[key].total) || 0);
  const points = (key) => ((series[key] && series[key].points) || []);
  const sumAt = (keys, i) => keys.reduce((n, k) => n + (points(k)[i] || 0), 0);

  const tiles = [
    { key: "requests", label: "Requests", family: "requests",
      total: total("verification_requests") + total("estimate_requests") + total("claim_followup_requests"),
      points: buckets.map((_, i) => sumAt(["verification_requests", "estimate_requests", "claim_followup_requests"], i)) },
    { key: "ai", label: "AI checks", family: "ai",
      total: total("ai_checks"), points: points("ai_checks") },
    { key: "manual", label: "Manual entries", family: "manual",
      total: total("manual_breakdowns") + total("estimate_uploads"),
      points: buckets.map((_, i) => sumAt(["manual_breakdowns", "estimate_uploads"], i)) },
  ];

  const byUser = data.by_user || {};
  const groupTotal = (g) => (byUser[g] || []).reduce((n, r) => n + r.total, 0);
  const ftcTotal = groupTotal("ftc");
  const officeTotal = groupTotal("office");
  const otherTotal = groupTotal("other");
  const unattributedTotal = Object.values(data.unattributed || {})
    .reduce((n, v) => n + (v || 0), 0);

  return (
    <>
      <div className="crm-act-tiles">
        {tiles.map((t) => (
          <button type="button" className="crm-act-tile" key={t.key} onClick={open}
                  title={`View ${t.label.toLowerCase()} in Analytics`}>
            <div className="crm-act-num" style={NUMERIC}>{t.total}</div>
            <div className="crm-act-label">{t.label}</div>
            {t.points.some((n) => n > 0) ? (
              <ReactApexChart type="area" height={34}
                              series={[{ name: t.label, data: t.points }]}
                              options={tileSparkOptions(t.family)} />
            ) : null}
          </button>
        ))}
      </div>
      <div className="crm-sub" style={{ marginTop: 10 }}>
        {/* Zero-check the tiles, not just ftc+office: work with no author (rows
            predating created_by, system-initiated jobs) and work by anyone
            outside both populations counts in the tiles above, so keying the
            sentence off ftc+office printed "No recorded activity" underneath
            non-zero figures. */}
        {tiles.every((t) => t.total === 0)
          ? "No recorded activity in the last 30 days."
          : [
              `${ftcTotal} by the FTC team`,
              `${officeTotal} by office staff`,
              otherTotal ? `${otherTotal} by others` : null,
              unattributedTotal ? `${unattributedTotal} unattributed` : null,
            ].filter(Boolean).join(" · ")}
        {" — "}
        <a href={analyticsHref}>see the full breakdown</a>
      </div>
    </>
  );
}

function Section({ title, count, extra, children }) {
  return (
    <div className="crm-section">
      <div className="crm-section-head">
        <span className="crm-section-title">{title}</span>
        {typeof count === "number" ? <span className="crm-section-count">{count}</span> : null}
        <span style={{ marginLeft: "auto" }}>{extra}</span>
      </div>
      <div className="crm-section-body">{children}</div>
    </div>
  );
}

// ── Notes ────────────────────────────────────────────────────────────────
function NotesTab({ officeId }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  // Task attributes for the note being composed.
  const [isTask, setIsTask] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [assigneeId, setAssigneeId] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [showDone, setShowDone] = useState(true);

  const load = useCallback(async () => {
    const r = await api.getOfficeNotes(officeId);
    if (r.status >= 200 && r.status < 300) setNotes(Array.isArray(r.json) ? r.json : []);
  }, [officeId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getTaskAssignees().then((r) => {
      if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setAssignees(r.json);
    }).catch(() => {});
  }, []);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const r = await api.addOfficeNote(officeId, body.trim(), {
      is_task: isTask,
      due_date: dueDate ? dueDate.format("YYYY-MM-DD") : null,
      assignee_id: assigneeId,
    });
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      setBody("");
      setIsTask(false);
      setDueDate(null);
      setAssigneeId(null);
      load();
    } else {
      message.error((r.json && r.json.detail) || "Could not add note.");
    }
  };

  const toggleDone = async (note, done) => {
    const r = await api.updateOfficeNote(officeId, note.id, { completed: done });
    if (r.status >= 200 && r.status < 300) load();
    else message.error("Could not update the task.");
  };

  const promote = async (note) => {
    const r = await api.updateOfficeNote(officeId, note.id, { is_task: !note.is_task });
    if (r.status >= 200 && r.status < 300) load();
    else message.error("Could not update the note.");
  };

  const visible = showDone ? notes : notes.filter((n) => !n.completed_at);
  const openCount = notes.filter((n) => n.is_task && !n.completed_at).length;

  return (
    <div>
      <Input.TextArea
        rows={3}
        placeholder="Add a note about this office…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />

      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Checkbox checked={isTask} onChange={(e) => setIsTask(e.target.checked)}>
          Make this a task
        </Checkbox>
        {isTask ? (
          <>
            <DatePicker
              size="small"
              placeholder="Due date"
              value={dueDate}
              onChange={setDueDate}
              style={{ width: 140 }}
            />
            <Select
              size="small"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign to"
              value={assigneeId}
              onChange={setAssigneeId}
              style={{ minWidth: 170 }}
              options={assignees.map((a) => ({ value: a.id, label: a.name || a.email }))}
            />
          </>
        ) : null}
        <Button
          type="primary"
          size="small"
          loading={busy}
          onClick={add}
          disabled={!body.trim()}
          style={{ marginLeft: "auto" }}
        >
          {isTask ? "Add task" : "Add note"}
        </Button>
      </div>

      <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="crm-sub">
          {openCount > 0 ? `${openCount} open task${openCount === 1 ? "" : "s"}` : "No open tasks"}
        </span>
        <Checkbox checked={showDone} onChange={(e) => setShowDone(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Show completed</span>
        </Checkbox>
      </div>

      <div className="crm-note-list" style={{ marginTop: 8 }}>
        {visible.length === 0 ? (
          <Empty description="No notes yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          visible.map((n) => {
            const done = !!n.completed_at;
            return (
              <div className="crm-note" key={n.id} style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  {n.is_task ? (
                    <Checkbox
                      checked={done}
                      onChange={(e) => toggleDone(n, e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                  ) : null}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        textDecoration: done ? "line-through" : "none",
                        color: done ? "#999" : undefined,
                      }}
                    >
                      {n.body}
                    </div>
                    <div className="crm-sub" style={{ marginTop: 4 }}>
                      {(n.author && n.author.name) || "—"} · {fmtDate(n.created_at)}
                      {n.is_task && n.due_date ? (
                        <Tag
                          color={n.is_overdue ? "red" : "blue"}
                          style={{ marginLeft: 8 }}
                        >
                          Due {n.due_date}
                        </Tag>
                      ) : null}
                      {n.is_task && n.assignee ? (
                        <Tag style={{ marginLeft: 4 }}>{n.assignee.name || n.assignee.email}</Tag>
                      ) : null}
                      {done && n.completed_by ? (
                        <span style={{ marginLeft: 8 }}>
                          done by {n.completed_by.name || n.completed_by.email}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {/* Turn a note you already wrote into a task (or back) without
                      retyping it — the note and the to-do are usually the same
                      sentence. */}
                  <Tooltip title={n.is_task ? "Make it a plain note" : "Make it a task"}>
                    <Button type="link" size="small" onClick={() => promote(n)}>
                      {n.is_task ? "Not a task" : "Make task"}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Tracked email addresses ──────────────────────────────────────────────
/**
 * Everything about mail to and from this clinic, in one tab.
 *
 * Two related but distinct things, deliberately labelled apart:
 *  - TRACKED ADDRESSES decide whose mail gets logged against this office;
 *  - FTC ACCESS decides who may write to it, and to whom.
 *
 * They used to be a page apart (this drawer vs FTC Users), which meant setting a
 * clinic up read as two unrelated errands.
 */
function CorrespondenceTab({ officeId, contacts, onChange }) {
  return (
    <>
      <TrackedEmailsTab officeId={officeId} contacts={contacts} onChange={onChange} />
      <div className="crm-tab-divider" />
      <OfficeFtcAccessPanel officeId={officeId} contacts={contacts} onChange={onChange} />
    </>
  );
}

function TrackedEmailsTab({ officeId, contacts, onChange }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    const r = await api.getTrackedEmails(officeId);
    if (r.status >= 200 && r.status < 300) setRows(Array.isArray(r.json) ? r.json : []);
  }, [officeId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) return;
    const r = await api.addTrackedEmail(officeId, email.trim(), label.trim());
    if (r.status >= 200 && r.status < 300) {
      setEmail("");
      setLabel("");
      load();
      onChange && onChange();
    } else {
      message.error((r.json && r.json.detail) || "Could not add address.");
    }
  };
  const toggle = async (row) => {
    await api.updateTrackedEmail(row.id, { is_active: !row.is_active });
    load();
    onChange && onChange();
  };
  const remove = async (row) => {
    await api.deleteTrackedEmail(row.id);
    load();
    onChange && onChange();
  };

  // Suggest the office's contact-people emails in the address field.
  const contactOptions = (contacts || [])
    .filter((c) => c.email)
    .map((c) => ({
      value: c.email,
      label: (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={{ fontWeight: 600 }}>{c.email}</span>
          {c.name || c.title ? (
            <span className="crm-sub" style={{ fontSize: 11 }}>
              {[c.name, c.title].filter(Boolean).join(" · ")}
            </span>
          ) : null}
        </div>
      ),
    }));
  // Match the typed text against each contact's email/name/title (look the contact
  // up by option.value — a standard field antd always passes to filterOption).
  const filterContact = (input, option) => {
    const q = (input || "").toLowerCase();
    const c = (contacts || []).find((x) => x.email === option.value) || {};
    return `${c.email || ""} ${c.name || ""} ${c.title || ""}`.toLowerCase().includes(q);
  };

  return (
    <div className="corr-section">
      <div className="corr-head">
        <div className="corr-head-text">
          <h4>Tracked addresses</h4>
          <p className="crm-sub">
            Mail to or from these is logged under this office&rsquo;s conversation.
          </p>
        </div>
      </div>
      <div className="corr-addrow">
        <AutoComplete
          style={{ flex: 1 }}
          options={contactOptions}
          value={email}
          onChange={(val) => setEmail(val)}
          onSelect={(val) => {
            // Picking a contact prefills the label with "Name - Title".
            const c = (contacts || []).find((x) => x.email === val);
            if (c) setLabel([c.name, c.title].filter(Boolean).join(" - "));
          }}
          filterOption={filterContact}
          placeholder="email@practice.com — or pick a contact"
          // The office drawer sits at z-index 10001 (global .ant-drawer-open rule);
          // the popup defaults to ~1050 and would render behind it. Lift it above.
          dropdownStyle={{ zIndex: 10050 }}
        />
        <Input placeholder="Label (optional)" style={{ maxWidth: 160 }} value={label} onChange={(e) => setLabel(e.target.value)} />
        <Button type="primary" icon={<PlusOutlined />} onClick={add}>Add</Button>
      </div>
      <Table
        size="small" rowKey="id" pagination={false}
        locale={{ emptyText: <div className="crm-empty">No tracked addresses.</div> }}
        columns={[
          { title: "Email", dataIndex: "email" },
          { title: "Label", dataIndex: "label", render: (v) => v || "—" },
          {
            title: "Active", dataIndex: "is_active", width: 90, align: "center",
            render: (v, r) => <Switch size="small" checked={v} onChange={() => toggle(r)} />,
          },
          {
            title: "", key: "a", width: 70, align: "right",
            render: (_, r) => (
              <Popconfirm title="Remove?" onConfirm={() => remove(r)} okText="Remove" okButtonProps={{ danger: true }}>
                <Button size="small" danger type="text">Remove</Button>
              </Popconfirm>
            ),
          },
        ]}
        dataSource={rows}
      />
    </div>
  );
}

// The Conversation tab is the email list (EmailFeed); the compose modal and its
// recipient helpers live with it. Re-exported here for the existing test.
export { EmailComposeModal, recipientGroups } from "./EmailComposeModal";

// Manual per-readiness-step reminder: preview (rendered email + default
// recipients) → edit subject/body/recipients → send now. Reuses the CRM
// reminder engine (same template + send path as the scheduled beat).
function SendReminderModal({ open, onClose, officeId, step, contacts, onSent }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [hasTemplate, setHasTemplate] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!open || !step) return undefined;
    setLoading(true);
    api
      .getReadinessReminderPreview(officeId, step.key)
      .then((r) => {
        if (cancelled) return;
        if (r.status >= 200 && r.status < 300 && r.json) {
          setSubject(r.json.subject || "");
          setBody(r.json.body_html || "");
          setRecipients(r.json.recipients || []);
          setHasTemplate(!!r.json.has_template);
          setEmailEnabled(r.json.email_enabled !== false);
        } else {
          message.error((r.json && r.json.detail) || "Couldn't load the reminder.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, officeId]);

  const contactOptions = (contacts || [])
    .filter((c) => c.email)
    .map((c) => ({
      value: c.email,
      label: [c.email, [c.name, c.title].filter(Boolean).join(" · ")].filter(Boolean).join(" — "),
    }));

  const send = async () => {
    if (!recipients.length) {
      message.error("Add at least one recipient.");
      return;
    }
    if (!body.trim()) {
      message.error("The message is empty.");
      return;
    }
    setBusy(true);
    const r = await api.sendReadinessReminder(officeId, step.key, {
      subject,
      body_html: body,
      recipients,
    });
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      message.success("Reminder sent.");
      onSent && onSent();
    } else if (r.status === 503) {
      message.warning("Email integration isn't enabled yet.");
    } else {
      message.error((r.json && r.json.detail) || "Send failed.");
    }
  };

  return (
    <Modal
      title={step ? `Send reminder — ${step.label}` : "Send reminder"}
      open={open}
      onCancel={onClose}
      onOk={send}
      okText="Send reminder"
      confirmLoading={busy}
      okButtonProps={{ disabled: loading || !emailEnabled || !recipients.length }}
      width={640}
      zIndex={10050}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <>
          {!emailEnabled ? (
            <div style={{ marginBottom: 10, color: "#d46b08" }}>
              Email integration isn't enabled — sending is disabled.
            </div>
          ) : null}
          {!hasTemplate ? (
            <div style={{ marginBottom: 10, color: "#8c8c8c", fontSize: 12 }}>
              No email template is set up for this step yet — this is a generic message you can edit.
            </div>
          ) : null}

          <div style={{ marginBottom: 6, fontWeight: 600 }}>Recipients</div>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            value={recipients}
            onChange={setRecipients}
            options={contactOptions}
            placeholder="Add recipients — search contacts or type an email"
            tokenSeparators={[",", " "]}
            optionFilterProp="label"
            dropdownStyle={{ zIndex: 10060 }}
          />

          <Input
            addonBefore="Subject"
            style={{ marginTop: 10 }}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <HtmlBodyEditor
            value={body}
            onChange={setBody}
            rows={8}
            placeholder="Message (HTML allowed)…"
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
}

// ── Drawer ───────────────────────────────────────────────────────────────

export default function OfficeDetailDrawer({ officeId, practiceId, onClose, initialTab, initialMessageId }) {
  // Which tab to land on. The Practices task column deep-links to "notes"; every
  // other caller gets the overview as before.
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  useEffect(() => { setActiveTab(initialTab || "overview"); }, [initialTab, officeId]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [reminderStep, setReminderStep] = useState(null); // { key, label } of the step to nudge
  const [dormantOpen, setDormantOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  const load = useCallback(async () => {
    if (!officeId) return;
    setLoading(true);
    const r = await api.getOfficeDetail(officeId);
    setLoading(false);
    if (r.status >= 200 && r.status < 300) setData(r.json);
    else { setData(null); message.error("Failed to load office."); }
  }, [officeId]);

  useEffect(() => {
    if (officeId) { setData(null); setShowTimeline(false); load(); }
  }, [officeId, load]);

  // Re-read the header (plan and trial chips) without the loading spinner, which
  // would unmount the open tab.
  const refresh = useCallback(async () => {
    if (!officeId) return;
    const r = await api.getOfficeDetail(officeId);
    if (r.status >= 200 && r.status < 300) setData(r.json);
  }, [officeId]);

  const doVerifyEmail = async (userId) => {
    setVerifyBusy(userId);
    const r = await api.verifyUserEmail(userId);
    setVerifyBusy(null);
    if (r.status >= 200 && r.status < 300) { message.success("Email verified."); load(); }
    else message.error((r.json && r.json.detail) || "Failed to verify email.");
  };
  const doResend = async (id) => {
    const r = await api.resendInvite(id);
    if (r.status >= 200 && r.status < 300) { message.success("Invite resent."); load(); }
    else message.error((r.json && r.json.detail) || "Failed to resend.");
  };
  const doRevoke = async (id) => {
    const r = await api.revokeInvite(id);
    if (r.status >= 200 && r.status < 300) { message.success("Invite revoked."); load(); }
    else message.error("Failed to revoke.");
  };
  // This office only — the rest of the practice is untouched either way.
  const doReactivate = async () => {
    setReactivating(true);
    const r = await api.reactivateOffice(officeId);
    setReactivating(false);
    if (r.status >= 200 && r.status < 300) { message.success("Office reactivated."); load(); }
    else message.error((r.json && r.json.detail) || "Couldn't reactivate.");
  };
  // Demo mode: on adds the two demo patients and lends this office the demo
  // source's SunLife/ProviderConnect logins; off removes the patients.
  const doDemoMode = async (on) => {
    setDemoBusy(true);
    const r = on ? await api.enableOfficeDemoMode(officeId) : await api.disableOfficeDemoMode(officeId);
    setDemoBusy(false);
    if (r.status >= 200 && r.status < 300) {
      message.success(on ? "Demo mode on. The demo patients were added." : "Demo mode off. The demo patients were removed.");
      load();
    } else message.error((r.json && r.json.detail) || "Couldn't change demo mode.");
  };

  // Absent until the office detail carries demo_mode; the switch hides then.
  const demo = data ? data.demo_mode : undefined;
  const stage = (data && data.onboarding_stage) || {};
  const checklist = (data && data.readiness) || [];
  // Counted over applicable items only — steps the practice registered without
  // aren't theirs to finish (see lib/readiness.js).
  const readiness = readinessCounts(checklist);
  const readyDone = readiness.done;
  const registeredWithout = planNote(data && data.onboarding_plan);
  const contact = (data && data.contact) || {};
  const tracked = ((data && data.tracked_emails) || []).filter((t) => t.is_active).map((t) => t.email);
  const effectivePracticeId = practiceId || (data && data.practice && data.practice.id);
  const fullyReady = isFullyReady(readiness);
  const sub = (data && data.subscription) || {};
  const timeline = (data && data.onboarding_timeline) || [];
  const tourUsers = (data && data.tour_by_user) || [];
  const invites = (data && data.invites) || [];
  const portalCoverage = (data && data.portal_credentials) || {};
  const portals = portalCoverage.portals || [];
  const portalsPending = portals.filter((p) => !p.collected);
  const isDormant = !!(data && data.dormant);

  const overview = (
    <div>
      {/* Tags live on the PRACTICE's lead (one lead per practice), so an office
          edits the same set its siblings see. Lazy: fetched when the drawer opens. */}
      <Section title="Tags" extra={<span className="crm-sub">On the practice's lead</span>}>
        <div style={{ padding: "6px 8px" }}>
          {effectivePracticeId ? (
            <PracticeTagEditor practiceId={effectivePracticeId} max={8} size="small" />
          ) : (
            <div className="crm-empty">This office has no practice, so there is no lead to tag.</div>
          )}
        </div>
      </Section>

      <Section
        title="Registration readiness"
        extra={<span className="crm-sub">{readiness.total ? `${readyDone} of ${readiness.total}` : ""}</span>}
      >
        <div className="crm-check-list">
          {checklist.map((c) => (
            <div className={`crm-check-row${isApplicable(c) ? "" : " na"}`} key={c.key}>
              {!isApplicable(c)
                ? <MinusCircleOutlined className="crm-check-icon na" />
                : c.done ? <CheckCircleFilled className="crm-check-icon done" /> : <MinusCircleOutlined className="crm-check-icon todo" />}
              <span className={`crm-check-label${c.done && isApplicable(c) ? " done" : ""}`}>{c.label}</span>
              {!isApplicable(c)
                ? <span className="crm-check-detail">{NOT_APPLICABLE_LABEL}</span>
                : c.detail ? <span className="crm-check-detail">{c.detail}</span> : null}
              {!c.done && isApplicable(c) ? (
                <Button
                  type="link"
                  size="small"
                  icon={<MailOutlined />}
                  style={{ marginLeft: c.detail ? 8 : "auto", padding: 0 }}
                  onClick={() => setReminderStep({ key: c.key, label: c.label })}
                >
                  Send reminder
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        {!fullyReady ? (
          <Button
            type="primary" ghost icon={<ThunderboltOutlined />}
            style={{ marginTop: 12 }}
            disabled={!effectivePracticeId}
            onClick={() => setSetupOpen(true)}
          >
            Complete office setup
          </Button>
        ) : null}

        {timeline.length ? (
          <div className="crm-tl-history">
            <button type="button" className="crm-tl-toggle" onClick={() => setShowTimeline((v) => !v)}>
              {showTimeline ? "Hide step history" : "Show step history"}
            </button>
            {showTimeline ? (
              <Timeline
                style={{ marginTop: 14 }}
                items={timeline.map((s) => ({
                  color: s.status === "done" || s.status === "completed" ? "#6B43B5" : "#c9c4d8",
                  children: (
                    <div className="crm-tl-item">
                      <span className="crm-tl-stage">{s.stage || s.name}</span>
                      <span className="crm-tl-date">{fmtDay(s.date_completed)}</span>
                    </div>
                  ),
                }))}
              />
            ) : null}
          </div>
        ) : null}
      </Section>

      <Section
        title="Activity"
        extra={<span className="crm-sub">Last 30 days</span>}
      >
        <ActivitySummary officeId={officeId} />
      </Section>

      <Section
        title="Portal credentials"
        count={portals.length || undefined}
        extra={
          <span className="crm-sub">
            {portals.length ? `${portalCoverage.collected} of ${portals.length} collected` : ""}
          </span>
        }
      >
        {portals.length ? (
          <>
            <Table
              size="small"
              rowKey="portal_name"
              pagination={false}
              dataSource={portals}
              locale={{ emptyText: <div className="crm-empty">No portals configured.</div> }}
              columns={[
                {
                  title: "Portal",
                  dataIndex: "portal_name",
                  render: (name, row) =>
                    row.portal_url ? (
                      <a href={row.portal_url} target="_blank" rel="noopener noreferrer">{name}</a>
                    ) : (
                      name
                    ),
                },
                {
                  title: "Carriers",
                  dataIndex: "carrier_names",
                  render: (names) => <span className="crm-sub">{(names || []).join(", ")}</span>,
                },
                {
                  title: "Login",
                  dataIndex: "collected",
                  width: 100,
                  render: (collected) =>
                    collected ? <Tag color="green">Collected</Tag> : <Tag color="gold">Pending</Tag>,
                },
                {
                  title: "2FA",
                  dataIndex: "two_factor",
                  width: 190,
                  render: (tf) => <TwoFactorCell tf={tf} />,
                },
              ]}
            />
            {portalsPending.length ? (
              // Reuses the per-step reminder flow rather than adding a second
              // send path; the email's {portal_carriers} token already resolves
              // to exactly these outstanding portals.
              <Button
                type="link"
                size="small"
                icon={<MailOutlined />}
                style={{ paddingLeft: 0, marginTop: 8 }}
                onClick={() =>
                  setReminderStep({ key: "insurance_credentials", label: "Insurance credentials set up" })
                }
              >
                {`Request the ${portalsPending.length} missing login${portalsPending.length > 1 ? "s" : ""}`}
              </Button>
            ) : null}
          </>
        ) : (
          <div className="crm-empty">
            No portal catalogue configured — carriers need a portal link before logins can be tracked.
          </div>
        )}
      </Section>

      <Section title="Chatbot onboarding" extra={<span className="crm-sub">Treatment Planning tour</span>}>
        {tourUsers.length ? (
          <div className="crm-user-list">
            {tourUsers.map((r) => {
              const t = tourTag(r);
              return (
                <div className="crm-user-row" key={r.user.id}>
                  <div className="crm-user-id">
                    <div className="crm-user-name">
                      {r.user.name}
                      {r.user.is_owner ? <Tag color="purple" className="crm-owner-tag">Owner</Tag> : null}
                    </div>
                    <div className="crm-sub">{r.user.email}</div>
                    {r.user.email_verified === true ? (
                      <span className="crm-verified"><CheckCircleFilled /> Email verified</span>
                    ) : r.user.email_verified === false ? (
                      <Button
                        className="crm-verify-btn" type="link" size="small"
                        icon={<SafetyCertificateOutlined />}
                        loading={verifyBusy === r.user.id}
                        onClick={() => doVerifyEmail(r.user.id)}
                      >
                        Verify email
                      </Button>
                    ) : null}
                  </div>
                  <div className="crm-user-prog">
                    <Tag color={t.color}>{t.text}</Tag>
                    {r.status !== "not_started" ? (
                      <span className="crm-user-far">{tourHowFar(r)} / {TOTAL_STEPS} steps</span>
                    ) : null}
                  </div>
                  <div className="crm-user-dates">
                    <span><b>Started</b>{fmtDay(r.started_at)}</span>
                    <span><b>Last active</b>{fmtDay(r.last_activity)}</span>
                    <span><b>{r.status === "skipped" ? "Skipped" : "Completed"}</b>{fmtDay(r.completed_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="crm-empty">No team members yet.</div>
        )}
      </Section>

      <Section title="Invites" count={invites.length}>
        <Table
          size="small" rowKey="id" pagination={false}
          locale={{ emptyText: <div className="crm-empty">No invites sent.</div> }}
          columns={[
            { title: "Email", dataIndex: "receiver_email", render: (v) => <span className="crm-name">{v}</span> },
            { title: "State", dataIndex: "state", render: (v) => <Tag color={INVITE_COLOR[v] || "default"}>{v}</Tag> },
            {
              title: "", key: "a", align: "right",
              render: (_, r) => (
                <Space>
                  <Button size="small" disabled={r.is_used} onClick={() => doResend(r.id)}>Resend</Button>
                  <Button size="small" danger disabled={r.is_used || r.state === "cancelled"} onClick={() => doRevoke(r.id)}>Revoke</Button>
                </Space>
              ),
            },
          ]}
          dataSource={invites}
        />
      </Section>

      <Section title="Contact people">
        <ContactsEditor
          officeId={officeId}
          contacts={(data && data.contacts) || []}
          branchAdmin={contact.branch_admin}
          onSaved={load}
        />
      </Section>

      <Section title="Reminders">
        <RemindersControl officeId={officeId} reminders={(data && data.reminders) || {}} onSaved={load} />
      </Section>
    </div>
  );

  return (
    <Drawer
      closable={false}
      title={null}
      // The breakdown format review shows the sheet beside its blanks: wider.
      width={activeTab === "breakdown-format"
        ? Math.max(874, Math.min(1440, (typeof window !== "undefined" ? window.innerWidth : 1440) - 40))
        : 874}
      open={!!officeId}
      onClose={onClose}
      destroyOnClose
      className="crm-drawer"
      bodyStyle={{ padding: 0, background: "#f7f6fb" }}
    >
      <button className="crm-hero-close" onClick={onClose} aria-label="Close"><CloseOutlined /></button>
      {loading || !data ? (
        <div className="crm-drawer-loading"><Spin size="large" /></div>
      ) : (
        <div className="crm-detail">
          <div className="crm-hero">
            <div className="crm-hero-main">
              <div className="crm-hero-left">
                <div className="crm-hero-top">
                  <div className="crm-hero-avatar"><ShopOutlined /></div>
                  <div className="crm-hero-id">
                    <div className="crm-hero-name">{data.name}</div>
                    <div className="crm-hero-owner">
                      {data.practice ? `${data.practice.name}` : "—"}
                      {contact.branch_admin ? ` · ${contact.branch_admin.name}` : ""}
                    </div>
                  </div>
                </div>

                <div className="crm-hero-chips">
                  <Tag color={STAGE_COLOR[stage.key] || "default"}>{stage.label || "—"}</Tag>
                  {/* Steps switched off in CRM Settings when this practice registered. */}
                  {registeredWithout ? <Tag>{registeredWithout}</Tag> : null}
                  <Tag color="purple">
                    {sub.tier_title
                      ? `${sub.tier_title}${sub.subscription_status ? " · " + sub.subscription_status : ""}`
                      : "No subscription"}
                  </Tag>
                  {sub.trial ? (
                    <Tag color={sub.trial.exhausted ? "red" : "gold"}>
                      {sub.trial.exhausted
                        ? `Trial ended · ${sub.trial.used}/${sub.trial.quota}`
                        : `Trial · ${sub.trial.used}/${sub.trial.quota}`}
                    </Tag>
                  ) : null}
                  {isDormant ? <span className="crmd-pill is-large">Dormant</span> : null}
                  {demo && demo.enabled ? <span className="crmd-pill is-large">Demo mode</span> : null}
                </div>

                <div className="crmd-hero-actions">
                  {isDormant ? (
                    <Button size="small" loading={reactivating} onClick={doReactivate}>
                      Reactivate
                    </Button>
                  ) : (
                    <Button size="small" onClick={() => setDormantOpen(true)}>
                      Mark dormant
                    </Button>
                  )}
                  {demo ? (
                    <Popconfirm
                      title={demo.enabled
                        ? "Turn demo mode off? The demo patients are removed from this office."
                        : "Turn demo mode on? This adds 2 demo patients and uses Rethink's SunLife and ProviderConnect logins for this office's fetches."}
                      okText={demo.enabled ? "Turn off" : "Turn on"}
                      onConfirm={() => doDemoMode(!demo.enabled)}
                    >
                      <span className="crmd-demo-toggle">
                        <Switch size="small" checked={!!demo.enabled} loading={demoBusy} />
                        <span>Demo mode</span>
                      </span>
                    </Popconfirm>
                  ) : null}
                </div>
                {demo && demo.enabled ? (
                  <div className="crm-sub crmd-demo-line">
                    Demo patients: {(demo.patients || []).map((p) => `${p.name}${p.carrier ? ` (${p.carrier})` : ""}`).join(", ") || "none"}
                    {demo.lender ? ` · using ${demo.lender}'s SunLife and ProviderConnect logins` : ""}
                  </div>
                ) : null}

                <div className="crm-hero-contact">
                  <span><MailOutlined />{data.email || "—"}</span>
                  {data.phone ? <span><PhoneOutlined />{data.phone}</span> : null}
                  {data.address ? <span><EnvironmentOutlined />{data.address}</span> : null}
                  {data.office_id ? (
                    <span className="crm-hero-pms">
                      PMS integration ID: <code className="crm-code">{data.office_id}</code>
                      {" · PMS: "}
                      {data.practice_management_system
                        ? `${data.practice_management_system}${data.practice_management_system_version ? " " + data.practice_management_system_version : ""}`
                        : "Unknown"}
                    </span>
                  ) : null}
                </div>
              </div>

              {data.address ? (
                <div className="crm-hero-side">
                  <a
                    className="crm-hero-map-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <EnvironmentOutlined /> Open in Google Maps
                  </a>
                  <div className="crm-hero-map">
                    <iframe
                      title="Office location"
                      src={`https://www.google.com/maps?q=${encodeURIComponent(data.address)}&z=15&output=embed`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <ContactCarousel contacts={data.contacts || []} />

          <div style={{ padding: "0 20px 20px" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                { key: "overview", label: "Overview", children: overview },
                {
                  key: "users",
                  label: "Users & Billing",
                  children: (
                    <div>
                      <Section title="Users"><OfficeUsersPanel officeId={officeId} /></Section>
                      {/* Plan, complimentary access, the trial cap and special offers. */}
                      <OfficeBillingPanel officeId={officeId} onChanged={refresh} />
                    </div>
                  ),
                },
                { key: "notes", label: "Notes", children: <NotesTab officeId={officeId} /> },
                { key: "emails", label: "Correspondence", children: <CorrespondenceTab officeId={officeId} contacts={(data && data.contacts) || []} onChange={load} /> },
                { key: "conversation", label: "Conversation", children: <EmailFeed officeId={officeId} tracked={tracked} initialMessageId={initialMessageId} contacts={(data && data.contacts) || []} officeEmail={data && data.email} practice={data && data.practice} /> },
                { key: "reports", label: "Reports", children: <OfficeReportPanel officeId={officeId} compact /> },
                { key: "features", label: "Features", children: <OfficeFeaturesPanel officeId={officeId} /> },
                {
                  key: "breakdown-format",
                  label: "Breakdown format",
                  children: <OfficeBreakdownFormatPanel officeId={officeId} officeName={data && data.name} />,
                },
              ]}
            />
          </div>
        </div>
      )}

      <CompleteSetupModal
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        practiceId={effectivePracticeId}
        officeId={officeId}
        onDone={refresh}
      />

      <SendReminderModal
        open={!!reminderStep}
        step={reminderStep}
        officeId={officeId}
        contacts={(data && data.contacts) || []}
        onClose={() => setReminderStep(null)}
        onSent={() => { setReminderStep(null); load(); }}
      />

      <MarkDormantWizard
        open={dormantOpen}
        target={
          data
            ? { kind: "office", id: officeId, name: data.name, offices: [{ id: officeId, name: data.name, dormant: isDormant }] }
            : null
        }
        onClose={() => setDormantOpen(false)}
        onDone={load}
      />
    </Drawer>
  );
}

// Read-only 3-up carousel of the office's contact people, shown in the hero.
// One editable contact row (name / job title / phone + save + remove).
function ContactRow({ contact, onChanged }) {
  const [name, setName] = useState(contact.name || "");
  const [title, setTitle] = useState(contact.title || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [email, setEmail] = useState(contact.email || "");
  // Whether this person is texted/emailed when an agent is stuck waiting on a
  // carrier's 2FA code. Defaults on, matching the model.
  const [alerts, setAlerts] = useState(contact.receives_2fa_alerts !== false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(contact.name || ""); setTitle(contact.title || "");
    setPhone(contact.phone || ""); setEmail(contact.email || "");
    setAlerts(contact.receives_2fa_alerts !== false);
  }, [contact.id, contact.name, contact.title, contact.phone, contact.email,
      contact.receives_2fa_alerts]);

  const save = async () => {
    setBusy(true);
    const r = await api.updateOfficeContactCard(contact.id, {
      name, title, phone, email, receives_2fa_alerts: alerts });
    setBusy(false);
    if (r.status >= 200 && r.status < 300) { message.success("Contact saved."); onChanged && onChanged(); }
    else message.error("Could not save contact.");
  };
  const remove = async () => {
    const r = await api.deleteOfficeContact(contact.id);
    if (r.status >= 200 && r.status < 300) { message.success("Contact removed."); onChanged && onChanged(); }
    else message.error("Could not remove contact.");
  };
  return (
    <div className="crm-contact-edit-row">
      <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input placeholder="Job title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <Tooltip title="Send this person the verification code prompt (email + SMS) when an agent is waiting on a carrier's 2FA code">
        <Checkbox checked={alerts} onChange={(e) => setAlerts(e.target.checked)}>
          2FA alerts
        </Checkbox>
      </Tooltip>
      <Button size="small" onClick={save} loading={busy}>Save</Button>
      <Popconfirm title="Remove this contact?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={remove}>
        <Button size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    </div>
  );
}

// The Contact section: edit the office's contact people + add new ones.
function ContactsEditor({ officeId, contacts, branchAdmin, onSaved }) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim() && !title.trim() && !phone.trim() && !email.trim()) {
      message.error("Enter a name, title, phone, or email.");
      return;
    }
    setBusy(true);
    const r = await api.addOfficeContact(officeId, {
      name: name.trim(), title: title.trim(), phone: phone.trim(), email: email.trim(),
    });
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      setName(""); setTitle(""); setPhone(""); setEmail("");
      message.success("Contact added."); onSaved && onSaved();
    } else {
      message.error("Could not add contact.");
    }
  };

  return (
    <div className="crm-contacts-editor">
      {(contacts || []).map((c) => <ContactRow key={c.id} contact={c} onChanged={onSaved} />)}
      <div className="crm-contact-edit-row crm-contact-add">
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder="Job title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={add} loading={busy}>Add</Button>
      </div>
      {branchAdmin ? (
        <div className="crm-sub" style={{ marginTop: 10 }}>Branch admin: {branchAdmin.name} · {branchAdmin.email}</div>
      ) : null}
    </div>
  );
}

function RemindersControl({ officeId, reminders, onSaved }) {
  const [disabled, setDisabled] = useState(!!reminders.disabled);
  useEffect(() => { setDisabled(!!reminders.disabled); }, [reminders.disabled]);
  const toggle = async (v) => {
    setDisabled(v);
    const r = await api.updateOfficeReminders(officeId, { reminders_disabled: v });
    if (r.status >= 200 && r.status < 300) onSaved && onSaved();
    else { setDisabled(!v); message.error("Could not update reminders."); }
  };
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <Switch checked={disabled} onChange={toggle} />
      <span>{disabled ? "Readiness reminders paused for this office" : "Readiness reminders on"}</span>
    </div>
  );
}
